import { toast } from 'vue-sonner';
import type { AssistantSuggestionApplyInput } from '~/lib/assistantSuggestionUi';
import type { AssistantAppliedAction, AssistantSuggestion } from '~/types/models';

type AssistantClientContext = {
    id: string;
    name: string;
};

type ApplySuggestionInput = {
    suggestion: AssistantSuggestion;
    client: AssistantClientContext;
    relatedSuggestions?: AssistantSuggestion[];
    onUseReply?: (reply: string) => void;
    projectIdOverride?: string;
    selectedTaskSuggestions?: AssistantSuggestion[];
    applyConfig?: AssistantSuggestionApplyInput;
    contextKey?: string;
};

export function useAssistantActions() {
    const authStore = useAuthStore();
    const userStore = useUserStore();
    const projectStore = useProjectStore();
    const taskStore = useTaskStore();
    const tenantStore = useTenantStore();
    const { $auth } = useNuxtApp();

    const currentUserId = computed(() =>
        authStore.user?.uid || userStore.user?.id || $auth.currentUser?.uid || '',
    );

    function ensureLoggedInUser(): string {
        if (!currentUserId.value) {
            throw new Error('You must be logged in to apply assistant suggestions.');
        }

        return currentUserId.value;
    }

    function buttonLabelForSuggestion(kind: string, relatedTaskCount = 0): string {
        if (kind === 'reply_draft') return 'Use Reply';
        if (relatedTaskCount > 0 && kind === 'quote_review') return 'Apply Suggestion';
        if (['follow_up', 'quote_review', 'risk_flag'].includes(kind)) return 'Create Task';
        if (kind === 'new_project' || kind === 'new_task') return 'Apply Suggestion';
        return 'Apply Suggestion';
    }

    function useReplySuggestion(reply?: string, onUseReply?: (reply: string) => void) {
        if (!reply) return;

        if (onUseReply) {
            onUseReply(reply);
            toast.message('Reply draft added.');
            return;
        }

        void copyReplySuggestion(reply);
    }

    async function copyReplySuggestion(reply?: string) {
        if (!reply) return;

        if (!import.meta.client || !navigator?.clipboard) {
            toast.message('Clipboard is unavailable on this device right now.');
            return;
        }

        await navigator.clipboard.writeText(reply);
        toast.message('Reply draft copied to clipboard.');
    }

    async function refreshAssistantRecords() {
        await Promise.all([
            projectStore.fetchProjects({ force: true }),
            taskStore.fetchTasks({ force: true }),
        ]);
    }

    async function undoAppliedAction(actionId: string) {
        const tenantId = tenantStore.currentTenantId;
        const token = await $auth.currentUser?.getIdToken();
        if (!tenantId || !token) {
            throw new Error('Your session has expired. Please sign in again.');
        }
        await $fetch(`/api/ai/actions/${encodeURIComponent(actionId)}/undo`, {
            method: 'POST',
            headers: { authorization: `Bearer ${token}` },
            body: { tenantId },
        });
        await refreshAssistantRecords();
        toast.success('Assistant action undone.');
    }

    function appliedActionLabel(
        suggestion: AssistantSuggestion,
        result: AssistantAppliedAction,
        explicitProjectId?: string,
    ): string {
        if (result.idempotentReplay) return 'This assistant action was already applied.';
        if (result.projectId && result.taskIds.length > 0 && !explicitProjectId) {
            return 'Project and suggested tasks created.';
        }
        if (result.projectId && result.taskIds.length > 0 && explicitProjectId) {
            return result.taskIds.length === 1
                ? 'Task added to the chosen project.'
                : 'Selected tasks added to the chosen project.';
        }
        if (suggestion.kind === 'new_project') return 'Project created from assistant suggestion.';
        return 'Task created from assistant suggestion.';
    }

    async function applyRecordedAction(input: ApplySuggestionInput): Promise<AssistantAppliedAction> {
        const tenantId = tenantStore.currentTenantId;
        const token = await $auth.currentUser?.getIdToken();
        const contextKey = String(input.contextKey || input.applyConfig?.contextKey || '').trim();
        if (!tenantId || !token) {
            throw new Error('Your session has expired. Please sign in again.');
        }
        if (!contextKey) {
            throw new Error('This suggestion is missing its analysis context. Refresh the assistant and try again.');
        }

        const selectedTaskIds = input.selectedTaskSuggestions?.map(item => item.id)
            || input.applyConfig?.selectedTaskIds
            || [];
        return await $fetch<AssistantAppliedAction>('/api/ai/actions/apply', {
            method: 'POST',
            headers: { authorization: `Bearer ${token}` },
            body: {
                tenantId,
                clientId: input.client.id,
                contextKey,
                suggestion: input.suggestion,
                relatedSuggestions: input.relatedSuggestions || [],
                selectedTaskIds,
                destination: input.applyConfig?.destination || 'default',
                projectId: input.applyConfig?.projectId || input.projectIdOverride || input.suggestion.projectId,
            },
        });
    }

    async function applySuggestion(input: ApplySuggestionInput) {
        const {
            suggestion,
            client,
            relatedSuggestions = [],
            onUseReply,
            projectIdOverride,
            selectedTaskSuggestions = [],
            applyConfig,
            contextKey,
        } = input;
        const explicitProjectId = applyConfig?.projectId || projectIdOverride;

        if (suggestion.kind === 'reply_draft') {
            useReplySuggestion(suggestion.suggestedReply, onUseReply);
            return;
        }

        ensureLoggedInUser();
        const result = await applyRecordedAction({
            ...input,
            contextKey,
            selectedTaskSuggestions,
            applyConfig,
        });
        await refreshAssistantRecords();
        toast.success(appliedActionLabel(suggestion, result, explicitProjectId), {
            duration: result.canUndo && !result.idempotentReplay ? 10_000 : 5_000,
            ...(result.canUndo && !result.idempotentReplay
                ? {
                    action: {
                        label: 'Undo',
                        onClick: () => {
                            void undoAppliedAction(result.actionId).catch((error: any) => {
                                toast.error(error?.data?.statusMessage || error?.message || 'Unable to undo this action.');
                            });
                        },
                    },
                }
                : {}),
        });
    }

    return {
        buttonLabelForSuggestion,
        copyReplySuggestion,
        applySuggestion,
    };
}
